import { useEffect, useMemo, useState } from 'react';
import { Box, Chip } from '@mui/material';
import { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { useDispatch } from 'react-redux';
import { CommonDatagrid } from '../../components/DataGrid';
import { RefreshButton } from '../../components/Buttons/RefreshButton';
import {
  CustomerAccountDialog,
  OTP_BYPASS_USER_TYPE_NAME,
} from '../../components/Dialog/CustomerAccountDialog';
import { showNotification } from '../../store/Reducers/NotificationSnackbarSlice';
import {
  CustomerAccountManagementService,
  ICustomerAccount,
  IUserType,
} from '../../api/services/customerAccountManagementService';

export const CustomerAccountManagementPage = () => {
  const dispatch = useDispatch();

  const [accounts, setAccounts] = useState<ICustomerAccount[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalElements, setTotalElements] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [userTypes, setUserTypes] = useState<IUserType[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<ICustomerAccount | null>(null);
  const [isOpenDialog, setIsOpenDialog] = useState(false);

  useEffect(() => {
    CustomerAccountManagementService.getUserTypes()
      .then(setUserTypes)
      .catch(() => {
        // the type dropdown just stays empty; search still works
      });
  }, []);

  const load = async (p = page, s = pageSize, q = search) => {
    setLoading(true);
    try {
      const res = await CustomerAccountManagementService.searchAccounts(p, s, q || undefined);
      setAccounts(res.content ?? []);
      setTotalElements(res.page?.totalElements ?? 0);
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to load customer accounts.',
          severity: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(page, pageSize, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, search]);

  const handlePageChange = (newPage: number) => setPage(newPage);
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(0);
  };
  const handleSearch = (q: string) => {
    setSearch(q.trim());
    setPage(0);
  };

  const onRowClick = (params: GridRowParams<ICustomerAccount>) => {
    setSelectedAccount(params.row);
    setIsOpenDialog(true);
  };

  const onAccountUpdated = (updated: ICustomerAccount) => {
    setSelectedAccount(updated);
    setAccounts((prev) => prev.map((a) => (a.user_pkid === updated.user_pkid ? updated : a)));
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'user_fname',
        headerName: 'Name',
        flex: 1,
        minWidth: 180,
        sortable: false,
        valueGetter: (_value, row: ICustomerAccount) =>
          `${row.user_fname ?? ''} ${row.user_lname ?? ''}`.trim(),
      },
      {
        field: 'user_phone',
        headerName: 'Phone',
        flex: 1,
        minWidth: 140,
        sortable: false,
      },
      {
        field: 'user_email',
        headerName: 'Email',
        flex: 1.5,
        minWidth: 200,
        sortable: false,
      },
      {
        field: 'userType',
        headerName: 'User Type',
        flex: 1,
        minWidth: 180,
        sortable: false,
        renderCell: (params: GridRenderCellParams<ICustomerAccount>) => {
          const typeName = params.row.userType?.usertyp_name;
          if (!typeName) return null;
          return (
            <Chip
              label={typeName}
              size="small"
              color={typeName === OTP_BYPASS_USER_TYPE_NAME ? 'warning' : 'default'}
            />
          );
        },
      },
    ],
    [],
  );

  return (
    <Box sx={{ p: 3 }}>
      <CommonDatagrid
        data={accounts}
        columns={columns}
        rowId="user_pkid"
        serverPagination
        page={page}
        pageSize={pageSize}
        rowCount={totalElements}
        loading={loading}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onChangeSearchKeyword={handleSearch}
        onRowClick={onRowClick}
        searchbarPlaceholder="Search by name, phone or email..."
        headerActionButtons={<RefreshButton onClick={() => load(page, pageSize, search)} />}
      />

      {selectedAccount && (
        <CustomerAccountDialog
          selectedAccount={selectedAccount}
          userTypes={userTypes}
          isOpenDialog={isOpenDialog}
          setIsOpenDialog={setIsOpenDialog}
          onAccountUpdated={onAccountUpdated}
        />
      )}
    </Box>
  );
};
